import { useCallback, useEffect, useState, type RefObject } from 'react';
import {
  type EmbeddedMode,
  type RonanToLoaderMessage,
} from './useParentMessaging';
import { isPhone } from '../utils/platform';

function postToLoader(message: RonanToLoaderMessage) {
  // These messages carry no flow data, and the parent's origin is only known
  // (and verified) after ronan:init, so they go to any parent.
  window.parent.postMessage(message, '*');
}

/**
 * Tell the loader how to show the iframe. Call it after useParentMessaging so
 * Ronan already listens for `ronan:init` when the loader reacts to the mode.
 */
export function useAnnounceEmbeddedMode(enabled: boolean, mode: EmbeddedMode) {
  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.add('embedded');
    postToLoader({ type: 'ronan:mode', mode });
  }, [enabled, mode]);
}

/** Tell the loader the iframe may be shown, once `ready` turns true. */
export function useAnnounceReadyToShow(ready: boolean) {
  useEffect(() => {
    if (ready) postToLoader({ type: 'ronan:show' });
  }, [ready]);
}

/** Card mode: report the card's height whenever it changes. */
export function useReportCardHeight(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(() =>
      postToLoader({
        type: 'ronan:resize',
        height: Math.ceil(element.getBoundingClientRect().height),
      }),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
}

/**
 * Drawer mode: open when the website opens the flow, and tell the loader
 * when the person closes it so the full-screen iframe stops covering the page.
 * The loader can also close it itself (`ronan:hide`).
 * Starts closed, because the loader preloads Ronan before that; opening
 * then plays the slide-up the person sees. The Studio's preview, which has no
 * loader, starts it open.
 */
export function useDrawerOpenState(initiallyOpen = false) {
  const [open, setOpen] = useState(initiallyOpen);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      if (event.data?.type === 'ronan:open') setOpen(true);
      if (event.data?.type === 'ronan:hide') setOpen(false);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    postToLoader({ type: 'ronan:close' });
  }, []);

  return { open, close };
}

/**
 * Ronan crashed outside the card or drawer, so nothing on screen can close
 * the drawer: close it now, and again whenever the website reopens it. The
 * card blocks nothing, so it shows the error inline.
 */
export function leaveAfterCrash() {
  if (!isPhone()) {
    postToLoader({ type: 'ronan:show' });
    return;
  }
  postToLoader({ type: 'ronan:close' });
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    if (event.data?.type === 'ronan:open')
      postToLoader({ type: 'ronan:close' });
  });
}
